/**
 * i4 htmlGeneric.pas Statistics line under Traits Analysed (@650–@655).
 * Default path only — custom `statistics_string` is out of scope (ILRX-4452).
 */

export type EbvStatisticsCounts = {
  numberOfHerds?: number | null;
  progenyCount?: number | null;
  scanProgenyCount?: number | null;
  carcaseProgenyCount?: number | null;
  daughtersCount?: number | null;
};

export type EbvStatisticsLine = {
  label: string;
  body: string;
};

const EBV_STATISTICS_LABEL = 'Statistics:';

const EBV_STATISTICS_SEGMENTS = [
  {
    key: 'numberOfHerds',
    suppressToken: 'nonumherds',
    format: (n: number) => `Herds Analysed: ${n}`,
  },
  {
    key: 'progenyCount',
    suppressToken: 'noprogenycnt',
    format: (n: number) => `Progeny Analysed: ${n}`,
  },
  {
    key: 'scanProgenyCount',
    suppressToken: 'noscanprogenycnt',
    format: (n: number) => `Scan Progeny Analysed: ${n}`,
  },
  {
    key: 'carcaseProgenyCount',
    suppressToken: 'nocarcprogenycnt',
    format: (n: number) => `Carcase Progeny Analysed: ${n}`,
  },
  {
    key: 'daughtersCount',
    suppressToken: 'nodtrscnt',
    format: (n: number) => `Daughters Analysed: ${n}`,
  },
] as const satisfies ReadonlyArray<{
  key: keyof EbvStatisticsCounts;
  suppressToken: string;
  format: (n: number) => string;
}>;

function hasEbvBoxOptionToken(
  ebvBoxOptionsRaw: string | null | undefined,
  token: string,
): boolean {
  return (ebvBoxOptionsRaw ?? '').toLowerCase().includes(token);
}

function isPositiveCount(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * Build the EBV Statistics line. Returns null when no segments remain after
 * count and `ebvBoxOptions` suppress rules (do not emit a bare label).
 */
export function formatEbvStatisticsLine(
  counts: EbvStatisticsCounts,
  ebvBoxOptionsRaw?: string | null,
): EbvStatisticsLine | null {
  const parts: string[] = [];

  for (const segment of EBV_STATISTICS_SEGMENTS) {
    if (hasEbvBoxOptionToken(ebvBoxOptionsRaw, segment.suppressToken)) {
      continue;
    }
    const value = counts[segment.key];
    if (!isPositiveCount(value)) continue;
    parts.push(segment.format(value));
  }

  if (parts.length === 0) return null;
  // i4 language templates include a trailing comma; drop it for display.
  return { label: EBV_STATISTICS_LABEL, body: parts.join(', ') };
}
